import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import {
  AcademicManagementFollowUpQuery,
  AcademicManagementReportType,
  ExportAcademicManagementFollowUpQuery,
} from 'domain/models';
import {
  ExportAcademicManagementGroups,
  ExportAcademicManagementInternshipLocations,
  ExportAcademicManagementProfessionals,
  ExportAcademicManagementTasks,
  GetAcademicManagementGroups,
  GetAcademicManagementInternshipLocations,
  GetAcademicManagementProfessionals,
  GetAcademicManagementTasks,
} from 'domain/usecases';
import { notifyError } from 'presentation/app/components/notify';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  AcademicOverviewSectionState,
  loadFilters,
  loadGroups,
  loadInternshipLocations,
  loadProfessionals,
  loadTasks,
} from 'presentation/config/store/reports/academic-overview-slice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { downloadReport } from '../common/helpers/download-report';
import { IAcademicOverviewFilters } from '../models/academic-overview-filters';

const DEFAULT_PAGE = 1;
const DEFAULT_LENGTH = 10;

interface IProps {
  getGroups: GetAcademicManagementGroups;
  getTasks: GetAcademicManagementTasks;
  getInternshipLocations: GetAcademicManagementInternshipLocations;
  getProfessionals: GetAcademicManagementProfessionals;
  exportGroups: ExportAcademicManagementGroups;
  exportTasks: ExportAcademicManagementTasks;
  exportInternshipLocations: ExportAcademicManagementInternshipLocations;
  exportProfessionals: ExportAcademicManagementProfessionals;
}

export const useAcademicOverview = ({
  getGroups,
  getTasks,
  getInternshipLocations,
  getProfessionals,
  exportGroups,
  exportTasks,
  exportInternshipLocations,
  exportProfessionals,
}: IProps) => {
  const dispatch = useDispatch();
  const { filters, groups, tasks, internshipLocations, professionals } =
    useAppSelector((state) => state.academicOverview);
  const [activeTab, setActiveTab] =
    useState<AcademicManagementReportType>('groups');
  const [exportingReport, setExportingReport] =
    useState<AcademicManagementReportType>();
  const submittedParamsRef = useRef<AcademicManagementFollowUpQuery>(
    filters
      ? {
          entityHashId: filters.entity?.value,
          courseHashId: filters.course?.value,
          semesterId: filters.semester?.value,
          curricularUnitHashId: filters.unit?.value,
          groupHashIds: filters.groups?.length
            ? filters.groups.map((group) => group.value)
            : undefined,
          start: filters.starts,
          end: filters.ends,
        }
      : undefined,
  );

  const saveFilters = useCallback(
    (params: IAcademicOverviewFilters) => dispatch(loadFilters(params)),
    [dispatch],
  );

  const requestSection = useCallback(
    async <T>(
      execute: (
        params: AcademicManagementFollowUpQuery,
      ) => Promise<{ total: number; data: T[] }>,
      section: AcademicOverviewSectionState<T>,
      updateSection: (state: AcademicOverviewSectionState<T>) => void,
      params: AcademicManagementFollowUpQuery,
      filtersKey: string,
    ) => {
      updateSection({ ...section, status: STATUSPAGE.LOADING });

      try {
        const response = await execute(params);
        updateSection({
          ...section,
          data: response.data || [],
          total: response.total || 0,
          page: params.page || DEFAULT_PAGE,
          length: params.length || DEFAULT_LENGTH,
          search: params.search,
          loadedForFiltersKey: filtersKey,
          status: STATUSPAGE.DONE,
        });
      } catch (err) {
        updateSection({
          ...section,
          data: [],
          total: 0,
          status: STATUSPAGE.DONE,
        });
        notifyError(err.message);
      }
    },
    [],
  );

  const requestTab = useCallback(
    async (
      tab: AcademicManagementReportType,
      overrides: Partial<AcademicManagementFollowUpQuery> = {},
      force = false,
    ) => {
      const submittedParams = submittedParamsRef.current;
      if (!submittedParams) return;

      const filtersKey = JSON.stringify(submittedParams);
      const section =
        tab === 'groups'
          ? groups
          : tab === 'tasks'
            ? tasks
            : tab === 'internship-locations'
              ? internshipLocations
              : professionals;

      if (!force && section.loadedForFiltersKey === filtersKey) return;

      const params = {
        ...submittedParams,
        page: section.page,
        length: section.length,
        search: section.search,
        ...overrides,
      };

      switch (tab) {
        case 'groups':
          await requestSection(
            getGroups.execute.bind(getGroups),
            groups,
            (state) => dispatch(loadGroups(state)),
            params,
            filtersKey,
          );
          break;
        case 'tasks':
          await requestSection(
            getTasks.execute.bind(getTasks),
            tasks,
            (state) => dispatch(loadTasks(state)),
            params,
            filtersKey,
          );
          break;
        case 'internship-locations':
          await requestSection(
            getInternshipLocations.execute.bind(getInternshipLocations),
            internshipLocations,
            (state) => dispatch(loadInternshipLocations(state)),
            params,
            filtersKey,
          );
          break;
        case 'professionals':
          await requestSection(
            getProfessionals.execute.bind(getProfessionals),
            professionals,
            (state) => dispatch(loadProfessionals(state)),
            params,
            filtersKey,
          );
          break;
        default:
          break;
      }
    },
    [
      getGroups,
      getInternshipLocations,
      getProfessionals,
      getTasks,
      dispatch,
      groups,
      internshipLocations,
      professionals,
      requestSection,
      tasks,
    ],
  );

  const submitFilters = useCallback(
    async (params: AcademicManagementFollowUpQuery) => {
      submittedParamsRef.current = params;
      await requestTab(
        activeTab,
        { page: DEFAULT_PAGE, length: DEFAULT_LENGTH, search: undefined },
        true,
      );
    },
    [activeTab, requestTab],
  );

  const changeTab = useCallback(
    async (tab: AcademicManagementReportType) => {
      setActiveTab(tab);
      await requestTab(tab);
    },
    [requestTab],
  );

  const changePage = useCallback(
    (page: number) => requestTab(activeTab, { page }, true),
    [activeTab, requestTab],
  );

  const changeRowsPerPage = useCallback(
    (length: number) =>
      requestTab(activeTab, { page: DEFAULT_PAGE, length }, true),
    [activeTab, requestTab],
  );

  const search = useMemo(
    () =>
      debounce(
        (value: string) =>
          requestTab(
            activeTab,
            { page: DEFAULT_PAGE, search: value || undefined },
            true,
          ),
        DEFAULT_DEBOUNCE_DELAY,
      ),
    [activeTab, requestTab],
  );

  useEffect(
    () => () => {
      search.clear();
    },
    [search],
  );

  const exportReport = useCallback(
    async (
      report: AcademicManagementReportType,
      params: ExportAcademicManagementFollowUpQuery,
    ) => {
      const exporters = {
        groups: exportGroups,
        tasks: exportTasks,
        'internship-locations': exportInternshipLocations,
        professionals: exportProfessionals,
      };

      setExportingReport(report);
      try {
        const response = await exporters[report].execute(params);
        downloadReport(response.url, {
          filename: `gestao-academica-${report}.xlsx`,
        });
      } catch (err) {
        notifyError(err.message);
      } finally {
        setExportingReport(undefined);
      }
    },
    [exportGroups, exportInternshipLocations, exportProfessionals, exportTasks],
  );

  return {
    activeTab,
    exportingReport,
    filters,
    groups,
    tasks,
    internshipLocations,
    professionals,
    saveFilters,
    submitFilters,
    changeTab,
    changePage,
    changeRowsPerPage,
    search,
    exportReport,
  };
};
